import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Cookies from "js-cookie";
import { Tilbake, Sidetopp } from "../ny-kundesamhandling/Skjema";
import SettSkjema from "../components/dekkhotell/SettSkjema";
import SesongSmsValg from "../components/dekkhotell/SesongSmsValg";
import { skrivUtEtikett } from "../utils/dekkhotellEtikett";
import {
  SETT_LABEL,
  LAGERSTED_LABEL,
  lagerstedForButikk,
  TOMT_SETT,
  settTekst,
  kundeNavn,
  settMerkeCls,
  fmtDato,
} from "../constants/dekkhotell";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Om høsten legges sommerdekkene inn, om våren vinterdekkene.
function settSomLeggesInnNa(d = new Date()) {
  const m = d.getMonth(); // 0 = januar
  return m >= 8 || m <= 1 ? "sommer" : "vinter";
}
const motsatt = (s) => (s === "sommer" ? "vinter" : "sommer");

async function hentJson(url, opts) {
  const r = await fetch(url, opts);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || "Noe gikk galt");
  return data;
}

function SettMerke({ sett }) {
  return (
    <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${settMerkeCls(sett)}`}>
      {SETT_LABEL[sett] || sett}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Ny innlagring
// ---------------------------------------------------------------------------
// Plassene per sted settes under Verktøy → Hotellkapasitet. Et fullt sted stopper ikke
// innlagringen (lagerboden tar resten), men gir en advarsel ved valget.
function stedErFullt(sted, perSted, plasserPerSted) {
  const grense = plasserPerSted?.[sted];
  return typeof grense === "number" && (perSted?.[sted] || 0) >= grense;
}

function NyInnlagring({ forvalgtKundeId, onOpprettet, onAvbryt, perSted, plasserPerSted }) {
  const [sok, setSok] = useState("");
  const [treff, setTreff] = useState([]);
  const [kunde, setKunde] = useState(null);
  const [sykler, setSykler] = useState([]);
  const [bikeId, setBikeId] = useState("");
  const [sykkel, setSykkel] = useState("");
  const [lagersted, setLagersted] = useState(lagerstedForButikk(Cookies.get("butikkid")));
  const [plassering, setPlassering] = useState("");
  const [serviceRef, setServiceRef] = useState("");
  const [betaltTil, setBetaltTil] = useState("");
  const [lagret, setLagret] = useState({ ...TOMT_SETT, sett: settSomLeggesInnNa() });
  const [medPaSykkel, setMedPaSykkel] = useState(true);
  const [paSykkel, setPaSykkel] = useState({
    ...TOMT_SETT,
    sett: motsatt(settSomLeggesInnNa()),
    pigger: settSomLeggesInnNa() === "sommer",
  });
  const [sesongSms, setSesongSms] = useState(null);
  const [feil, setFeil] = useState("");
  const [lagrer, setLagrer] = useState(false);

  useEffect(() => {
    if (!forvalgtKundeId) return;
    hentJson(`${API_BASE_URL}/customers/${forvalgtKundeId}`)
      .then(setKunde)
      .catch(() => setFeil("Fant ikke kunden"));
  }, [forvalgtKundeId]);

  useEffect(() => {
    if (kunde || sok.trim().length < 2) return setTreff([]);
    const t = setTimeout(() => {
      hentJson(`${API_BASE_URL}/customers/search?q=${encodeURIComponent(sok.trim())}`)
        .then(setTreff)
        .catch(() => setTreff([]));
    }, 250);
    return () => clearTimeout(t);
  }, [sok, kunde]);

  useEffect(() => {
    if (!kunde?._id) return setSykler([]);
    hentJson(`${API_BASE_URL}/bikes?customerId=${encodeURIComponent(kunde._id)}`)
      .then(setSykler)
      .catch(() => setSykler([]));
  }, [kunde]);

  // Har kunden svart før (annen sykkel), er svaret valgt på forhånd.
  useEffect(() => {
    setSesongSms(null);
    if (!kunde?._id) return;
    hentJson(`${API_BASE_URL}/dekkhotell/sesong-sms/${kunde._id}`)
      .then((d) => setSesongSms(d.verdi))
      .catch(() => setSesongSms(null));
  }, [kunde]);

  // Settet på sykkelen er alltid det motsatte av det som legges inn.
  useEffect(() => {
    setPaSykkel((p) => ({ ...p, sett: motsatt(lagret.sett) }));
  }, [lagret.sett]);

  async function lagre(e) {
    e.preventDefault();
    setFeil("");
    if (!kunde) return setFeil("Velg kunde først.");
    if (!lagersted) return setFeil("Velg hvor settet lagres.");
    if (sesongSms === null) return setFeil("Spør kunden om SMS når det er tid for dekkskift.");
    setLagrer(true);
    try {
      const post = await hentJson(`${API_BASE_URL}/dekkhotell`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId: kunde._id,
          bikeId: bikeId || undefined,
          sykkel,
          butikkid: parseInt(Cookies.get("butikkid"), 10) || undefined,
          lagersted,
          plassering,
          serviceRef,
          betaltTil: betaltTil || undefined,
          lagret,
          paSykkel: medPaSykkel ? paSykkel : undefined,
          sesongSms,
        }),
      });
      onOpprettet(post);
    } catch (err) {
      setFeil(err.message);
    } finally {
      setLagrer(false);
    }
  }

  const felt = "w-full min-w-0 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30";
  const etikett = "mb-1 block text-sm font-semibold text-slate-800";

  return (
    <form onSubmit={lagre} className="rounded-2xl border border-slate-200 bg-white p-3 mb-4 space-y-5">
      <h2 className="text-lg font-bold text-slate-900">Legg inn i dekkhotellet</h2>

      <section>
        <h3 className="text-sm font-semibold text-slate-700 mb-2">1. Kunde og sykkel</h3>
        {kunde ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-medium">{kundeNavn(kunde)}</span>
            <span className="text-slate-600 text-sm">{kunde.phoneNumber}</span>
            {!forvalgtKundeId && (
              <button type="button" onClick={() => setKunde(null)} className="text-sm text-indigo-700 underline">
                Bytt kunde
              </button>
            )}
          </div>
        ) : (
          <div className="relative max-w-md">
            <label htmlFor="dh-kundesok" className={etikett}>Søk på navn eller telefon</label>
            <input
              id="dh-kundesok"
              type="search"
              value={sok}
              onChange={(e) => setSok(e.target.value)}
              className={felt}
              autoComplete="off"
            />
            {treff.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow">
                {treff.map((k) => (
                  <li key={k._id}>
                    <button
                      type="button"
                      onClick={() => setKunde(k)}
                      className="w-full text-left px-3 py-2 hover:bg-indigo-50"
                    >
                      {kundeNavn(k)} <span className="text-slate-600 text-sm">{k.phoneNumber}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {kunde && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="dh-sykkelkort" className={etikett}>Sykkelkort</label>
              <select id="dh-sykkelkort" value={bikeId} onChange={(e) => setBikeId(e.target.value)} className={felt}>
                <option value="">Ingen / ikke registrert</option>
                {sykler.map((b) => (
                  <option key={b._id} value={b._id}>
                    {[b.merke, b.modell, b.elsykkel ? "(elsykkel)" : ""].filter(Boolean).join(" ")} · {b.shortId}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="dh-sykkel" className={etikett}>Sykkel (fritekst)</label>
              <input
                id="dh-sykkel"
                type="text"
                placeholder="F.eks. svart Trek Allant+ 7"
                value={sykkel}
                onChange={(e) => setSykkel(e.target.value)}
                className={felt}
              />
            </div>
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-slate-700 mb-2">2. Settet som lagres hos oss</h3>
        <SettSkjema idPrefix="dh-lagret" verdi={lagret} onChange={setLagret} />
      </section>

      <section>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
          <input type="checkbox" checked={medPaSykkel} onChange={(e) => setMedPaSykkel(e.target.checked)} />
          3. Settet som monteres / sitter på sykkelen
        </label>
        {medPaSykkel && <SettSkjema idPrefix="dh-pasykkel" verdi={paSykkel} onChange={setPaSykkel} lasSett />}
      </section>

      <section className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label htmlFor="dh-lagersted" className={etikett}>Lagres hos</label>
          <select
            id="dh-lagersted"
            value={lagersted}
            onChange={(e) => setLagersted(e.target.value)}
            aria-describedby={stedErFullt(lagersted, perSted, plasserPerSted) ? "dh-lagersted-fullt" : undefined}
            className={felt}
          >
            <option value="">Velg sted</option>
            {Object.entries(LAGERSTED_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}{stedErFullt(k, perSted, plasserPerSted) ? " (fullt)" : ""}</option>
            ))}
          </select>
          {stedErFullt(lagersted, perSted, plasserPerSted) && (
            <p id="dh-lagersted-fullt" role="status" className="mt-1 text-sm font-semibold text-amber-900">
              {LAGERSTED_LABEL[lagersted]} er fullt ({perSted[lagersted] || 0} av {plasserPerSted[lagersted]} sett). Velg et annet sted, eller lagerboden.
            </p>
          )}
        </div>
        <div>
          <label htmlFor="dh-plassering" className={etikett}>Plassering (reol/hylle)</label>
          <input
            id="dh-plassering"
            type="text"
            placeholder="F.eks. Reol B, hylle 3"
            value={plassering}
            onChange={(e) => setPlassering(e.target.value)}
            className={felt}
          />
        </div>
        <div>
          <label htmlFor="dh-serviceref" className={etikett}>Verkstedordre (betaling)</label>
          <input
            id="dh-serviceref"
            type="text"
            placeholder="F.eks. RSS-1234"
            value={serviceRef}
            onChange={(e) => setServiceRef(e.target.value)}
            className={felt}
          />
        </div>
        <div>
          <label htmlFor="dh-betalttil" className={etikett}>Lagring betalt til</label>
          <input
            id="dh-betalttil"
            type="date"
            value={betaltTil}
            onChange={(e) => setBetaltTil(e.target.value)}
            className={felt}
          />
        </div>
      </section>

      <SesongSmsValg
        idPrefix="dh-ny"
        verdi={sesongSms}
        onChange={(v) => {
          setSesongSms(v);
          setFeil("");
        }}
        disabled={!kunde}
      />

      {feil && (
        <p role="alert" className="text-sm text-red-700">{feil}</p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={lagrer}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60"
        >
          {lagrer ? "Lagrer…" : "Legg inn og skriv ut etikett"}
        </button>
        <button type="button" onClick={onAvbryt} className="px-4 py-2 rounded-lg border border-slate-300">
          Avbryt
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Sesongpåminnelse på SMS
// ---------------------------------------------------------------------------
function Paminnelse({ onSendt }) {
  const [sett, setSett] = useState(settSomLeggesInnNa() === "sommer" ? "vinter" : "sommer");
  const [info, setInfo] = useState(null);
  const [tekst, setTekst] = useState("");
  const [status, setStatus] = useState("");
  const [sender, setSender] = useState(false);

  useEffect(() => {
    setInfo(null);
    setStatus("");
    hentJson(`${API_BASE_URL}/dekkhotell/paminnelse?sett=${sett}`)
      .then((d) => {
        setInfo(d);
        setTekst(d.standardtekst);
      })
      .catch((e) => setStatus(e.message));
  }, [sett]);

  const mottakere = info?.mottakere || [];
  const antall = mottakere.filter((m) => m.harTelefon && m.sesongSms === true).length;
  const sagtNei = mottakere.filter((m) => m.sesongSms === false).length;
  const ikkeSpurt = mottakere.filter((m) => m.sesongSms == null).length;

  async function send() {
    if (!window.confirm(`Sende påminnelse på SMS til ${antall} kunder?`)) return;
    setSender(true);
    setStatus("");
    try {
      const r = await hentJson(`${API_BASE_URL}/dekkhotell/paminnelse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sett, tekst }),
      });
      setStatus(
        `Sendt: ${r.sendt}. Feilet: ${r.feilet}. Uten telefon: ${r.utenTelefon}. Sagt nei: ${r.sagtNei}. Ikke spurt: ${r.ikkeSpurt}.`
      );
      onSendt?.();
      const d = await hentJson(`${API_BASE_URL}/dekkhotell/paminnelse?sett=${sett}`);
      setInfo(d);
    } catch (e) {
      setStatus(e.message);
    } finally {
      setSender(false);
    }
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-3 mb-4">
      <h2 className="text-lg font-bold text-slate-900 mb-1">Sesongpåminnelse på SMS</h2>
      <p className="text-sm text-slate-600 mb-3">
        Går til kunder som har settet på lager, har sagt ja til SMS om dekkskift og ikke har fått
        påminnelse de siste 90 dagene.
        Bruk {"{nr}"} for nummeret. Ikke skriv navn i teksten.
      </p>
      <div className="flex flex-wrap gap-3 items-end mb-3">
        <div>
          <label htmlFor="dh-pam-sett" className="mb-1 block text-sm font-semibold text-slate-800">Hvem</label>
          <select
            id="dh-pam-sett"
            value={sett}
            onChange={(e) => setSett(e.target.value)}
            className="p-2 border border-slate-300 rounded-lg"
          >
            <option value="sommer">Har sommerdekk på lager (våren)</option>
            <option value="vinter">Har vinterdekk på lager (høsten)</option>
          </select>
        </div>
        <p className="text-sm text-slate-700 py-2">
          {info ? `${antall} mottakere` : "Henter …"}
          {info && sagtNei > 0 && ` · ${sagtNei} har sagt nei`}
          {info && ikkeSpurt > 0 && ` · ${ikkeSpurt} er ikke spurt (åpne posten og velg)`}
        </p>
      </div>
      <label htmlFor="dh-pam-tekst" className="mb-1 block text-sm font-semibold text-slate-800">Tekst</label>
      <textarea
        id="dh-pam-tekst"
        rows={3}
        value={tekst}
        onChange={(e) => setTekst(e.target.value)}
        className="w-full p-2 border border-slate-300 rounded-lg"
      />
      <p className="text-xs text-slate-600 mt-1">
        Ca. {tekst.replaceAll("{fornavn}", "").replaceAll("{nr}", "DH-0000").length} tegn med
        nummer (over 160 blir to SMS-er per kunde)
      </p>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <button
          type="button"
          onClick={send}
          disabled={sender || antall === 0}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60"
        >
          {sender ? "Sender…" : "Send påminnelse"}
        </button>
        {status && <p role="status" className="text-sm text-slate-800">{status}</p>}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Oversikten
// ---------------------------------------------------------------------------
export default function Dekkhotell() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const forvalgtKundeId = params.get("kunde") || "";
  const [visNy, setVisNy] = useState(!!forvalgtKundeId);
  const [visPaminnelse, setVisPaminnelse] = useState(false);
  const [status, setStatus] = useState("lagret");
  const [sett, setSett] = useState("");
  const [sted, setSted] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ poster: [], antall: { sommer: 0, vinter: 0 }, perSted: {} });
  const [laster, setLaster] = useState(true);
  const [feil, setFeil] = useState("");

  const hent = useCallback(async () => {
    setLaster(true);
    setFeil("");
    try {
      const qs = new URLSearchParams({ status });
      if (sett) qs.set("sett", sett);
      if (sted) qs.set("lagersted", sted);
      if (q.trim()) qs.set("q", q.trim());
      setData(await hentJson(`${API_BASE_URL}/dekkhotell?${qs}`));
    } catch (e) {
      setFeil(e.message);
    } finally {
      setLaster(false);
    }
  }, [status, sett, sted, q]);

  useEffect(() => {
    const t = setTimeout(hent, 250);
    return () => clearTimeout(t);
  }, [hent]);

  function opprettet(post) {
    setVisNy(false);
    if (forvalgtKundeId) setParams({});
    skrivUtEtikett(post);
    navigate(`/dekkhotell/${post._id}`);
  }

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake />
      <Sidetopp
        ikon="dekk"
        farge="lager"
        tittel="Dekkhotell"
        undertittel={<>
          <p className="text-sm text-slate-600">
            På lager nå: {data.antall.sommer} sommersett · {data.antall.vinter} vintersett
          </p>
          <p className="text-sm text-slate-600">
            {Object.entries(LAGERSTED_LABEL)
              .map(([k, v]) => {
                const grense = data.plasserPerSted?.[k];
                return `${v}: ${data.perSted?.[k] || 0}${typeof grense === "number" ? ` av ${grense}` : ""}`;
              })
              .join(" · ")}
          </p>
        </>}
      >
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setVisPaminnelse((v) => !v)}
            className="inline-flex min-h-[2.5rem] items-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
            aria-expanded={visPaminnelse}
          >
            Sesongpåminnelse
          </button>
          <button
            type="button"
            onClick={() => setVisNy((v) => !v)}
            className="inline-flex min-h-[2.5rem] items-center gap-2 rounded-xl bg-emerald-700 px-3 py-1.5 font-bold text-white hover:bg-emerald-800 disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
            aria-expanded={visNy}
          >
            + Ny innlagring
          </button>
        </div>
      </Sidetopp>

      {visPaminnelse && <Paminnelse onSendt={hent} />}
      {visNy && (
        <NyInnlagring
          forvalgtKundeId={forvalgtKundeId}
          perSted={data.perSted}
          plasserPerSted={data.plasserPerSted}
          onOpprettet={opprettet}
          onAvbryt={() => {
            setVisNy(false);
            if (forvalgtKundeId) setParams({});
          }}
        />
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="min-w-0 flex-1">
          <label htmlFor="dh-sok" className="sr-only">Søk</label>
          <input
            id="dh-sok"
            type="search"
            placeholder="Søk på DH-nr, kunde, telefon, plassering eller dekk"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full min-w-0 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30"
          />
        </div>
        <label htmlFor="dh-status" className="sr-only">Status</label>
        <select id="dh-status" value={status} onChange={(e) => setStatus(e.target.value)} className="p-2 border border-slate-300 rounded-lg">
          <option value="lagret">På lager</option>
          <option value="avsluttet">Utlevert</option>
          <option value="alle">Alle</option>
        </select>
        <label htmlFor="dh-sted" className="sr-only">Lagersted</label>
        <select id="dh-sted" value={sted} onChange={(e) => setSted(e.target.value)} className="p-2 border border-slate-300 rounded-lg">
          <option value="">Alle steder</option>
          {Object.entries(LAGERSTED_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <label htmlFor="dh-sett" className="sr-only">Sett</label>
        <select id="dh-sett" value={sett} onChange={(e) => setSett(e.target.value)} className="p-2 border border-slate-300 rounded-lg">
          <option value="">Alle sett</option>
          <option value="sommer">Sommerdekk på lager</option>
          <option value="vinter">Vinterdekk på lager</option>
        </select>
      </div>

      {feil && <p role="alert" className="text-red-700 mb-3">{feil}</p>}
      {laster ? (
        <p className="text-slate-600">Laster …</p>
      ) : data.poster.length === 0 ? (
        <p className="text-slate-600">Ingen treff.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-slate-200 rounded-xl text-sm">
            <thead>
              <tr className="bg-slate-100 text-left text-slate-700">
                <th className="px-3 py-2">Nr</th>
                <th className="px-3 py-2">Kunde</th>
                <th className="px-3 py-2">På lager</th>
                <th className="px-3 py-2">Ligger hos</th>
                <th className="px-3 py-2">Betalt til</th>
                <th className="px-3 py-2">Påminnet</th>
              </tr>
            </thead>
            <tbody>
              {data.poster.map((p) => (
                <tr key={p._id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2 font-semibold whitespace-nowrap">
                    <Link to={`/dekkhotell/${p._id}`} className="text-indigo-700 underline">{p.nr}</Link>
                  </td>
                  <td className="px-3 py-2">
                    {kundeNavn(p.kunde)}
                    <div className="text-slate-600">{p.kunde?.phoneNumber}</div>
                  </td>
                  <td className="px-3 py-2">
                    {p.status === "lagret" ? (
                      <>
                        <SettMerke sett={p.lagret?.sett} />
                        <div className="text-slate-700 mt-1">{settTekst({ ...p.lagret, sett: null })}</div>
                      </>
                    ) : (
                      <span className="text-slate-600">Utlevert {fmtDato(p.avsluttetDato)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {LAGERSTED_LABEL[p.lagersted]}
                    {p.plassering && <div className="text-slate-600">{p.plassering}</div>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDato(p.betaltTil)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDato(p.varsel?.sisteDato)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
